import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import Layout from '../components/Layout';
import PageTabBar from '../components/PageTabBar';
import StockSatelliteHeader from '../features/stock/StockSatelliteHeader';
import StockInvoiceDestinationBar from '../features/stock/StockInvoiceDestinationBar';
import { DEFAULT_DOUANE, DEFAULT_MARGE, recalcInvoiceProducts, isInvoicePhoneLine, getInvoiceImeiList, patchInvoiceImeis, countFilledImeis } from '../features/stock/stockInvoiceUtils';
import { STOCK_TABS } from './_stockTabs';
import { FileText, Loader2, Wrench, XCircle, CheckCircle, Check, Upload, Sparkles, Smartphone } from 'lucide-react';

const StockInvoiceImportPage = () => {
  const { api, user } = useAuth();
  const { t, formatCurrency, formatNumber } = useLanguage();
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [shipping, setShipping] = useState('');
  const [parsing, setParsing] = useState(false);
  const [recalculating, setRecalculating] = useState(false);
  const [data, setData] = useState(null);
  const [location, setLocation] = useState('depot');
  const [magasinId, setMagasinId] = useState(() => {
    try {
      return localStorage.getItem('pos_current_magasin_id') || user?.default_magasin_id || 'M1';
    } catch {
      return user?.default_magasin_id || 'M1';
    }
  });
  const [magasinsList, setMagasinsList] = useState([]);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const [douane, setDouane] = useState(DEFAULT_DOUANE);
  const [marge, setMarge] = useState(DEFAULT_MARGE);

  useEffect(() => {
    api.get('/magasins?active_only=true')
      .then((r) => setMagasinsList(r.data?.magasins || []))
      .catch(() => setMagasinsList([]));
  }, [api]);

  useEffect(() => {
    if (magasinId !== 'M1' && location === 'depot') {
      setLocation('boutique');
    }
  }, [magasinId, location]);

  const generateEan13 = () => {
    const digits = [2, 0, 0];
    for (let i = 0; i < 9; i++) digits.push(Math.floor(Math.random() * 10));
    const oddSum = digits.filter((_, i) => i % 2 === 0).reduce((a, b) => a + b, 0);
    const evenSum = digits.filter((_, i) => i % 2 === 1).reduce((a, b) => a + b, 0);
    digits.push((10 - ((oddSum + evenSum * 3) % 10)) % 10);
    return digits.join('');
  };

  const applyRecalc = useCallback(async (base, d = douane, m = marge) => {
    setRecalculating(true);
    try {
      const next = await recalcInvoiceProducts(api, {
        produits: base.produits,
        frais_port_total: base.frais_port_total,
        douane_pct: d,
        marge_pct: m,
      });
      setData({ ...base, ...next, fournisseur: base.fournisseur });
    } finally {
      setRecalculating(false);
    }
  }, [api, douane, marge]);

  const handleParse = async () => {
    if (!file) return;
    setParsing(true);
    setData(null);
    setResult(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const s = parseFloat(shipping) || 0;
      const response = await api.post(
        `/stock/import-invoice/parse?frais_port_total=${s}&douane_pct=${douane}&marge_pct=${marge}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120000 },
      );
      setData(response.data);
    } catch (error) {
      alert(error.response?.data?.detail || t('stockInvImport.errorAiAnalysis'));
    } finally {
      setParsing(false);
    }
  };

  const handleDouaneChange = async (val) => {
    const d = parseFloat(val) || 0;
    setDouane(d);
    if (data) await applyRecalc(data, d, marge);
  };

  const handleMargeChange = async (val) => {
    const m = parseFloat(val) || 0;
    setMarge(m);
    if (data) await applyRecalc(data, douane, m);
  };

  const handleRemove = async (idx) => {
    if (!data) return;
    const produits = data.produits.filter((_, i) => i !== idx);
    await applyRecalc({ ...data, produits });
  };

  const handleEan = (idx) => {
    if (!data) return;
    const p = [...data.produits];
    p[idx] = { ...p[idx], ean: generateEan13() };
    setData({ ...data, produits: p });
  };

  const handleMO = async (idx, value) => {
    if (!data) return;
    const p = [...data.produits];
    p[idx] = { ...p[idx], main_oeuvre: parseFloat(value) || 0 };
    await applyRecalc({ ...data, produits: p });
  };

  const handlePrixFinal = (idx, value) => {
    if (!data) return;
    const p = [...data.produits];
    p[idx] = { ...p[idx], prix_vente_final: parseFloat(value) || 0 };
    setData({ ...data, produits: p });
  };

  const handleImeiChange = (idx, imeiIdx, value) => {
    if (!data) return;
    const p = [...data.produits];
    const list = getInvoiceImeiList(p[idx]);
    list[imeiIdx] = value;
    p[idx] = patchInvoiceImeis(p[idx], list);
    setData({ ...data, produits: p });
  };

  const phoneLinesMissingImei = data?.produits?.filter(
    (p) => isInvoicePhoneLine(p) && countFilledImeis(p) < (p.quantite || 1),
  )?.length || 0;

  const handleConfirm = async () => {
    if (!data || !magasinId) return;
    if (phoneLinesMissingImei > 0) {
      const ok = window.confirm(
        t('stockInvImport.confirmMissingImei', { count: phoneLinesMissingImei })
      );
      if (!ok) return;
    }
    setImporting(true);
    try {
      const response = await api.post('/stock/import-invoice/confirm', {
        produits: data.produits,
        fournisseur: data.fournisseur,
        location,
        magasin_id: magasinId,
      });
      setResult(response.data);
      setData(null);
    } catch (error) {
      alert(error.response?.data?.detail || t('stockInvImport.errorImport'));
    } finally {
      setImporting(false);
    }
  };

  const handleMagasinChange = (code) => {
    setMagasinId(code);
    try { localStorage.setItem('pos_current_magasin_id', code); } catch { /* ignore */ }
  };

  const locationLabel = location === 'depot' ? t('stockInvImport.locationDepot') : t('stockInvImport.locationBoutique');

  const panel = 'rounded-xl border border-white/10 bg-[#151820]';

  return (
    <Layout>
      <div className="max-w-7xl mx-auto fade-in" data-testid="stock-invoice-import-page">
        <PageTabBar tabs={STOCK_TABS} activeColor="rose" />
        <StockSatelliteHeader
          title={t('stockInvImport.pageTitle')}
          subtitle={t('stockInvImport.pageSubtitle')}
          backTestId="invoice-back-to-stock"
        />

        {!data && !result && (
          <div className={`${panel} p-4 sm:p-6 max-w-2xl`} data-testid="invoice-upload-section">
            <div className="flex items-center gap-2 mb-4 text-xs text-gray-400">
              <Sparkles className="w-4 h-4 text-red-400" />
              {t('stockInvImport.visionHint')}
            </div>
            <div className="space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-gray-400 mb-2 font-semibold uppercase">{t('stockInvImport.invoiceLabel')}</label>
                  <div
                    className="relative border-2 border-dashed border-white/10 hover:border-red-500/40 rounded-xl p-6 text-center cursor-pointer transition-colors"
                    onClick={() => fileRef.current?.click()}
                  >
                    <input ref={fileRef} type="file" accept=".pdf,.png,.jpg,.jpeg,.webp" onChange={(e) => setFile(e.target.files?.[0] || null)} className="hidden" data-testid="invoice-file-input" />
                    {file ? (
                      <div>
                        <FileText className="w-8 h-8 text-red-400 mx-auto mb-2" />
                        <p className="text-sm text-white font-medium">{file.name}</p>
                        <p className="text-xs text-gray-500 mt-1">{t('stockInvImport.fileSize', { size: formatNumber(Math.round(file.size / 1024)) })}</p>
                      </div>
                    ) : (
                      <div>
                        <Upload className="w-8 h-8 text-gray-500 mx-auto mb-2" />
                        <p className="text-sm text-gray-400">{t('stockInvImport.clickToChoose')}</p>
                        <p className="text-xs text-gray-600 mt-1">PDF, PNG, JPG, WEBP</p>
                      </div>
                    )}
                  </div>
                </div>
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs text-gray-400 mb-2 font-semibold uppercase">
                      {t('stockInvImport.shippingTotalLabel')}
                      <span className="text-gray-600 font-normal ml-1">{t('stockInvImport.autoIfEmpty')}</span>
                    </label>
                    <input type="number" step="0.01" min="0" value={shipping} onChange={(e) => setShipping(e.target.value)} placeholder={t('stockInvImport.shippingPlaceholder')} className="w-full px-3 py-2.5 rounded-lg bg-black/30 border border-white/10 text-white text-sm min-h-[44px]" data-testid="invoice-shipping-input" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs text-orange-300 mb-1">{t('stockInvImport.customsPct')}</label>
                      <input type="number" min="0" max="200" step="1" value={douane} onChange={(e) => setDouane(parseFloat(e.target.value) || 0)} className="w-full px-3 py-2 rounded-lg bg-black/30 border border-orange-500/30 text-orange-300 text-sm text-center font-bold min-h-[44px]" data-testid="invoice-douane-input" />
                    </div>
                    <div>
                      <label className="block text-xs text-emerald-300 mb-1">{t('stockInvImport.marginPct')}</label>
                      <input type="number" min="0" max="500" step="1" value={marge} onChange={(e) => setMarge(parseFloat(e.target.value) || 0)} className="w-full px-3 py-2 rounded-lg bg-black/30 border border-emerald-500/30 text-emerald-300 text-sm text-center font-bold min-h-[44px]" data-testid="invoice-marge-input" />
                    </div>
                  </div>
                </div>
              </div>
              <StockInvoiceDestinationBar
                magasinsList={magasinsList}
                magasinId={magasinId}
                onMagasinChange={handleMagasinChange}
                location={location}
                onLocationChange={setLocation}
              />
              <button type="button" onClick={handleParse} disabled={!file || parsing || !magasinId} className="w-full min-h-[48px] py-3 rounded-lg bg-red-600 hover:bg-red-500 text-white font-bold flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed transition-all" data-testid="invoice-parse-btn">
                {parsing ? (<><Loader2 className="w-5 h-5 animate-spin" /> {t('stockInvImport.analyzing')}</>) : (<><FileText className="w-5 h-5" /> {t('stockInvImport.analyzeInvoice')}</>)}
              </button>
            </div>
          </div>
        )}

        {data && !result && (
          <div className="space-y-3" data-testid="invoice-preview-section">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {[
                { value: data.produits.length, label: t('stockInvImport.kpiProducts'), color: 'text-white' },
                { value: data.fournisseur || '—', label: t('stockInvImport.kpiSupplier'), color: 'text-red-400', small: true },
                { value: formatCurrency(data.frais_port_total), label: t('stockInvImport.kpiShippingTotal'), color: 'text-blue-400' },
                { value: formatCurrency(data.frais_port_par_article), label: t('stockInvImport.kpiShippingPerUnit'), color: 'text-purple-400' },
              ].map((kpi, i) => (
                <div key={i} className={`${panel} p-3 text-center`}>
                  <div className={`${kpi.small ? 'text-sm' : 'text-xl'} font-bold tabular-nums ${kpi.color}`}>{kpi.value}</div>
                  <div className="text-[10px] text-gray-500 uppercase">{kpi.label}</div>
                </div>
              ))}
            </div>

            <StockInvoiceDestinationBar
              magasinsList={magasinsList}
              magasinId={magasinId}
              onMagasinChange={handleMagasinChange}
              location={location}
              onLocationChange={setLocation}
              compact
            />

            <div className={`${panel} p-3 flex flex-wrap items-center gap-4`}>
              <div className="flex items-center gap-2">
                <label className="text-xs text-orange-300">{t('stockInvImport.customsPct')}</label>
                <input type="number" min="0" max="200" step="1" value={douane} onChange={(e) => handleDouaneChange(e.target.value)} disabled={recalculating} className="w-20 px-2 py-2 rounded-lg bg-black/30 border border-orange-500/30 text-orange-300 text-sm text-center font-bold" data-testid="invoice-douane-preview" />
              </div>
              <div className="flex items-center gap-2">
                <label className="text-xs text-emerald-300">{t('stockInvImport.marginPct')}</label>
                <input type="number" min="0" max="500" step="1" value={marge} onChange={(e) => handleMargeChange(e.target.value)} disabled={recalculating} className="w-20 px-2 py-2 rounded-lg bg-black/30 border border-emerald-500/30 text-emerald-300 text-sm text-center font-bold" data-testid="invoice-marge-preview" />
              </div>
              {recalculating && <span className="text-xs text-gray-500 flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> {t('stockInvImport.recalc')}</span>}
            </div>

            {phoneLinesMissingImei > 0 && (
              <div className="rounded-lg border border-amber-500/30 bg-amber-950/20 px-3 py-2 text-xs text-amber-200 flex items-center gap-2">
                <Smartphone className="w-4 h-4 shrink-0" />
                {t('stockInvImport.phonesCompleteImei', { count: phoneLinesMissingImei })}
              </div>
            )}

            <div className={`${panel} overflow-hidden`}>
              <div className="hidden lg:block overflow-x-auto">
                <table className="w-full text-sm" data-testid="invoice-products-table">
                  <thead>
                    <tr className="border-b border-white/10 text-gray-400 text-xs">
                      <th className="p-2 w-8" />
                      <th className="text-left p-2 whitespace-nowrap">{t('stockInvImport.colRef')}</th>
                      <th className="text-left p-2 min-w-[120px]">{t('stockInvImport.colDesignation')}</th>
                      <th className="text-center p-2">{t('stockInvImport.colQty')}</th>
                      <th className="text-right p-2 whitespace-nowrap">{t('stockInvImport.colPurchase')}</th>
                      <th className="text-right p-2 whitespace-nowrap">{t('stockInvImport.colPlusShipping')}</th>
                      <th className="text-right p-2 whitespace-nowrap">{t('stockInvImport.colPlusCustoms', { pct: douane })}</th>
                      <th className="text-right p-2 whitespace-nowrap">{t('stockInvImport.colPlusMargin', { pct: marge })}</th>
                      <th className="text-center p-2"><Wrench className="w-3 h-3 inline mr-1" />{t('stockInvImport.colLabor')}</th>
                      <th className="text-right p-2 text-amber-300 whitespace-nowrap">{t('stockInvImport.colFinalPrice')}</th>
                      <th className="text-center p-2">EAN</th>
                      <th className="text-left p-2 min-w-[130px]">IMEI</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.produits.map((p, idx) => {
                      const phone = isInvoicePhoneLine(p);
                      const imeis = getInvoiceImeiList(p);
                      const filled = countFilledImeis(p);
                      return (
                        <tr key={idx} className={`border-b border-white/5 hover:bg-white/5 ${phone ? 'bg-red-950/10' : ''}`}>
                          <td className="p-1">
                            <button type="button" onClick={() => handleRemove(idx)} className="p-1 rounded hover:bg-red-500/20 text-red-400/60 hover:text-red-400" title={t('stockInvImport.remove')} data-testid={`invoice-remove-${idx}`}>
                              <XCircle className="w-4 h-4" />
                            </button>
                          </td>
                          <td className="p-2 font-mono text-xs text-gray-400">{p.reference}</td>
                          <td className="p-2">
                            <div className="flex items-center gap-1">
                              {phone ? <Smartphone className="w-3 h-3 text-red-400 shrink-0" /> : p.is_piece ? <Wrench className="w-3 h-3 text-gray-500 shrink-0" /> : null}
                              <span className="text-white text-xs">{p.designation}</span>
                            </div>
                          </td>
                          <td className="p-2 text-center text-white font-bold">{p.quantite}</td>
                          <td className="p-2 text-right text-gray-300 tabular-nums whitespace-nowrap">{p.prix_achat_unitaire?.toFixed(2)}</td>
                          <td className="p-2 text-right text-blue-300 tabular-nums whitespace-nowrap">{p.cout_base?.toFixed(2)}</td>
                          <td className="p-2 text-right text-orange-300 tabular-nums whitespace-nowrap">{p.cout_douane?.toFixed(2)}</td>
                          <td className="p-2 text-right text-emerald-300 tabular-nums whitespace-nowrap">{p.prix_vente_calcule?.toFixed(2)}</td>
                          <td className="p-2 text-center">
                            {p.is_piece ? (
                              <input type="number" step="0.01" min="0" value={p.main_oeuvre || ''} onChange={(e) => handleMO(idx, e.target.value)} placeholder="0" className="w-16 px-1 py-1 rounded bg-black/30 border border-cyan-500/30 text-cyan-300 text-xs text-center" data-testid={`invoice-mo-${idx}`} />
                            ) : <span className="text-gray-600">—</span>}
                          </td>
                          <td className="p-2 text-right">
                            <input type="number" step="0.01" min="0" value={p.prix_vente_final} onChange={(e) => handlePrixFinal(idx, e.target.value)} className="w-20 px-1 py-1 rounded bg-black/30 border border-amber-500/30 text-amber-300 text-xs text-right font-bold tabular-nums" data-testid={`invoice-prix-final-${idx}`} />
                          </td>
                          <td className="p-2 text-center">
                            {p.ean ? (
                              <span className="text-xs text-emerald-400 font-mono">{p.ean.slice(-4)}</span>
                            ) : (
                              <button type="button" onClick={() => handleEan(idx)} className="px-2 py-1 rounded bg-white/5 hover:bg-white/10 text-xs text-gray-300" data-testid={`invoice-ean-${idx}`}>EAN</button>
                            )}
                          </td>
                          <td className="p-2">
                            {phone ? (
                              <div className="space-y-1 min-w-[120px]">
                                {imeis.map((imeiVal, ii) => (
                                  <input
                                    key={ii}
                                    type="text"
                                    value={imeiVal}
                                    onChange={(e) => handleImeiChange(idx, ii, e.target.value)}
                                    placeholder={`IMEI ${ii + 1}${filled > ii ? '' : ' *'}`}
                                    className={`w-full min-w-[120px] px-2 py-1 rounded bg-black/30 border text-xs font-mono ${filled > ii ? 'border-emerald-500/40 text-emerald-300' : 'border-red-500/40 text-white'}`}
                                    data-testid={`invoice-imei-${idx}-${ii}`}
                                  />
                                ))}
                              </div>
                            ) : (
                              <span className="text-gray-600 text-xs">—</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="lg:hidden divide-y divide-white/5" data-testid="invoice-products-cards">
                {data.produits.map((p, idx) => {
                  const phone = isInvoicePhoneLine(p);
                  const imeis = getInvoiceImeiList(p);
                  return (
                    <div key={idx} className={`p-3 space-y-2 ${phone ? 'bg-red-950/10' : ''}`}>
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <p className="font-mono text-[10px] text-gray-500">{p.reference}</p>
                          <p className="text-sm text-white font-semibold flex items-center gap-1">
                            {phone && <Smartphone className="w-3.5 h-3.5 text-red-400" />}
                            {p.designation}
                          </p>
                        </div>
                        <button type="button" onClick={() => handleRemove(idx)} className="p-1 text-red-400"><XCircle className="w-4 h-4" /></button>
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs">
                        <div><span className="text-gray-500">{t('stockInvImport.colQty')}</span> <strong className="text-white">{p.quantite}</strong></div>
                        <div><span className="text-gray-500">{t('stockInvImport.colPurchase')}</span> <strong className="text-gray-300">{formatCurrency(p.prix_achat_unitaire ?? 0)}</strong></div>
                        <div><span className="text-gray-500">{t('stockInvImport.colPlusShipping')}</span> <strong className="text-blue-300">{formatCurrency(p.cout_base ?? 0)}</strong></div>
                        <div><span className="text-gray-500">{t('stockInvImport.colPlusCustoms', { pct: douane })}</span> <strong className="text-orange-300">{formatCurrency(p.cout_douane ?? 0)}</strong></div>
                        <div><span className="text-gray-500">{t('stockInvImport.colPlusMargin', { pct: marge })}</span> <strong className="text-emerald-300">{formatCurrency(p.prix_vente_calcule ?? 0)}</strong></div>
                        {p.is_piece && (
                          <div className="col-span-2 sm:col-span-1">
                            <span className="text-gray-500">{t('stockInvImport.colLabor')}</span>
                            <input type="number" step="0.01" min="0" value={p.main_oeuvre || ''} onChange={(e) => handleMO(idx, e.target.value)} placeholder="0" className="mt-1 w-full px-2 py-1.5 rounded-lg bg-black/30 border border-cyan-500/30 text-cyan-300 text-xs" />
                          </div>
                        )}
                        <div className="col-span-2">
                          <span className="text-gray-500">{t('stockInvImport.colFinalPrice')}</span>
                          <input type="number" step="0.01" value={p.prix_vente_final} onChange={(e) => handlePrixFinal(idx, e.target.value)} className="mt-1 w-full px-2 py-2 rounded-lg bg-black/30 border border-amber-500/30 text-amber-300 font-bold" />
                        </div>
                        <div>
                          <span className="text-gray-500">EAN</span>
                          <div className="mt-1">
                            {p.ean ? (
                              <span className="text-emerald-400 font-mono">{p.ean}</span>
                            ) : (
                              <button type="button" onClick={() => handleEan(idx)} className="px-2 py-1 rounded bg-white/5 text-gray-300 text-xs">{t('stockInvImport.generateEan')}</button>
                            )}
                          </div>
                        </div>
                      </div>
                      {phone && (
                        <div className="space-y-1.5 pt-1 border-t border-white/5">
                          <p className="text-[10px] font-bold text-red-400 uppercase">IMEI *</p>
                          {imeis.map((imeiVal, ii) => (
                            <input
                              key={ii}
                              type="text"
                              value={imeiVal}
                              onChange={(e) => handleImeiChange(idx, ii, e.target.value)}
                              placeholder={t('stockInvImport.imeiDevicePlaceholder', { n: ii + 1 })}
                              className="w-full px-2 py-2 rounded-lg bg-black/30 border border-white/10 text-sm font-mono"
                              data-testid={`invoice-imei-mobile-${idx}-${ii}`}
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="flex gap-3 sticky bottom-4 z-10">
              <button type="button" onClick={() => setData(null)} className="flex-1 min-h-[48px] py-3 rounded-lg border border-white/10 bg-[#151820] hover:bg-white/5 text-white font-medium" data-testid="invoice-back-btn">{t('stockInvImport.back')}</button>
              <button type="button" onClick={handleConfirm} disabled={importing || data.produits.length === 0 || recalculating || !magasinId} className="flex-1 min-h-[48px] py-3 rounded-lg bg-red-600 hover:bg-red-500 text-white font-bold flex items-center justify-center gap-2 disabled:opacity-50" data-testid="invoice-confirm-btn">
                {importing ? (<><Loader2 className="w-5 h-5 animate-spin" /> {t('stockInvImport.importing')}</>) : (<><Check className="w-5 h-5" /> {t('stockInvImport.confirm')} · {magasinId} · {locationLabel} ({data.produits.length})</>)}
              </button>
            </div>
          </div>
        )}

        {result && (
          <div className={`${panel} p-6 max-w-lg mx-auto`} data-testid="invoice-result-section">
            <div className="text-center">
              <CheckCircle className="w-14 h-14 text-emerald-400 mx-auto mb-3" />
              <h3 className="text-xl font-bold text-white mb-2">{t('stockInvImport.importDone')}</h3>
              {(result.magasin_id || result.location) && (
                <p className="text-sm text-gray-400 mb-4">
                  {result.magasin_id} · {result.location === 'depot' ? t('stockInvImport.locationDepot') : t('stockInvImport.locationBoutique')}
                </p>
              )}
              <div className="flex justify-center gap-8 mb-4 flex-wrap">
                <div><span className="text-3xl font-bold text-emerald-400">{result.created}</span><p className="text-sm text-gray-500">{t('stockInvImport.created')}</p></div>
                <div><span className="text-3xl font-bold text-blue-400">{result.updated}</span><p className="text-sm text-gray-500">{t('stockInvImport.updated')}</p></div>
                {result.imei_registered > 0 && (
                  <div><span className="text-3xl font-bold text-red-400">{result.imei_registered}</span><p className="text-sm text-gray-500">{t('stockInvImport.imeiRegistered')}</p></div>
                )}
                {result.errors?.length > 0 && <div><span className="text-3xl font-bold text-red-400">{result.errors.length}</span><p className="text-sm text-gray-500">{t('stockInvImport.errors')}</p></div>}
              </div>
              {result.imei_missing?.length > 0 && (
                <div className="text-left mb-4 bg-amber-950/30 rounded-lg p-3 border border-amber-500/20">
                  <p className="text-xs font-bold text-amber-300 mb-2">{t('stockInvImport.imeiMissingTitle')}</p>
                  {result.imei_missing.map((m, i) => (
                    <p key={i} className="text-xs text-amber-200/90">{m.reference} · {t('stockInvImport.missingOfTotal', { missing: m.missing, total: m.quantite })}</p>
                  ))}
                </div>
              )}
              {result.errors?.length > 0 && (
                <div className="text-left mb-4 bg-red-950/30 rounded-lg p-3 border border-red-500/20">
                  {result.errors.map((e, i) => (<p key={i} className="text-xs text-red-300">{e}</p>))}
                </div>
              )}
              <div className="flex gap-3">
                <button type="button" onClick={() => { setResult(null); setFile(null); setShipping(''); }} className="flex-1 min-h-[48px] py-3 rounded-lg border border-white/10 text-gray-300 font-medium" data-testid="invoice-new-import-btn">{t('stockInvImport.newImport')}</button>
                <button type="button" onClick={() => navigate('/stock')} className="flex-1 min-h-[48px] py-3 rounded-lg bg-red-600 hover:bg-red-500 text-white font-bold" data-testid="invoice-go-stock-btn">{t('stockInvImport.backToStock')}</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default StockInvoiceImportPage;
